import React, { useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ProgressArc from './shared/ProgressArc';
import SectionBanner from './shared/SectionBanner';
import DilemmaRoomEngine from './engines/DilemmaRoomEngine';
import ChaosSimulatorEngine from './engines/ChaosSimulatorEngine';
import BrokenLessonEngine from './engines/BrokenLessonEngine';
import LessonBuilderEngine from './engines/LessonBuilderEngine';
import MisconceptionCatcherEngine from './engines/MisconceptionCatcherEngine';
import CuriosityExplorerEngine from './engines/CuriosityExplorerEngine';
import PriorityInboxEngine from './engines/PriorityInboxEngine';
import StaffRoomEngine from './engines/StaffRoomEngine';
import TrolleyEngine from './engines/TrolleyEngine';
import StressThermometerEngine from './engines/StressThermometerEngine';
import LikertSliderEngine from './engines/LikertSliderEngine';
import MemoryMatchEngine from './engines/MemoryMatchEngine';
import { questionById } from './questionBank.js';
import styles from './GamifiedQuestionRenderer.module.css';

const normalizeOriginalOptions = (options) =>
  (Array.isArray(options) ? options : []).map((option, index) => ({
    label: String(option?.label || option?.key || String.fromCharCode(65 + index)),
    text: String(option?.text || option?.label || option?.key || `Option ${index + 1}`),
    consequence: `Response ${String(option?.label || option?.key || String.fromCharCode(65 + index))} has been captured for scoring.`
  }));

const fallbackQuestion = (question, sectionName) => ({
  id: String(question?.id || 'fallback_question'),
  text: String(question?.text || 'Review this scenario and choose your best action.'),
  gameType: 'LIKERT_SLIDER',
  scene: {
    type: 'fallback',
    accent: '#264653',
    elements: {
      source: 'fallback'
    },
    animation: {}
  },
  narration: 'Select the response that best represents your decision in this scene.',
  options: normalizeOriginalOptions(question?.options),
  sectionKey: String(sectionName || '').toLowerCase()
});

const resolveEngineKey = (sectionName, traitName, gameType) => {
  const sectionKey = String(sectionName || '').toLowerCase();
  const traitKey = String(traitName || '').toLowerCase();

  // Memory match games
  const game = String(gameType || '').toUpperCase();
  if (game === 'MEMORY_MATCH') return 'memory_match';

  if (traitKey.includes('openness')) return 'openness';
  if (traitKey.includes('conscientiousness')) return 'conscientiousness';
  if (traitKey.includes('extraversion')) return 'extraversion';
  if (traitKey.includes('agreeableness')) return 'agreeableness';
  if (traitKey.includes('neuroticism')) return 'neuroticism';
  if (sectionKey.includes('ethics')) return 'ethics';
  if (sectionKey.includes('classroom')) return 'classroom';
  if (sectionKey.includes('digital')) return 'digital';
  if (sectionKey.includes('pedagogical')) return 'pedagogical';
  if (sectionKey.includes('subject')) return 'subject';

  if (game === 'TROLLEY') return 'agreeableness';
  if (game === 'STRESS_INBOX') return 'neuroticism';
  if (game === 'PRIORITY_DRAG' || game === 'SORT_BIN') return 'conscientiousness';
  if (game === 'CHAOS_CLICK' || game === 'HOTSPOT_IMAGE' || game === 'SPOTLIGHT') return 'classroom';
  if (game === 'SCENARIO_PANEL' || game === 'SPEECH_BUBBLE') return 'ethics';
  if (game === 'WORD_BUILD' || game === 'TIMELINE_DROP') return 'pedagogical';
  if (game === 'LIKERT_SLIDER' || game === 'METER_FILL' || game === 'DIAL_TURN') return 'fallback';

  return 'fallback';
};

const accentByKey = (key) => {
  if (key === 'ethics' || key === 'agreeableness') return '#e63946';
  if (key === 'classroom') return '#457b9d';
  if (key === 'pedagogical') return '#2a9d8f';
  if (key === 'digital') return '#6a4c93';
  if (key === 'subject') return '#f4a261';
  return '#264653';
};

const GamifiedQuestionRenderer = ({
  question,
  questionIndex,
  totalQuestions,
  onAnswer,
  sectionName,
  traitName,
  sectionQuestionIndex,
  sectionQuestionCount
}) => {
  const sourceQuestion = useMemo(
    () => questionById[question?.id] || fallbackQuestion(question, sectionName),
    [question, sectionName]
  );

  const gamifiedQuestion = useMemo(() => ({
    ...sourceQuestion,
    sceneType: sourceQuestion?.scene?.type || sourceQuestion?.sceneType || 'fallback',
    situation: String(sourceQuestion?.text || sourceQuestion?.situation || ''),
    narration:
      sourceQuestion?.narration ||
      sourceQuestion?.scene?.elements?.narration ||
      'Select the response that best represents your decision in this scene.',
    options: (Array.isArray(sourceQuestion?.options) ? sourceQuestion.options : []).map((option, index) => ({
      ...option,
      label: String(option?.label || option?.key || String.fromCharCode(65 + index)),
      text: String(option?.text || option?.actionText || `Option ${index + 1}`),
      actionText: String(option?.text || option?.actionText || `Option ${index + 1}`),
      consequence:
        option?.consequence ||
        `Selected ${String(option?.label || option?.key || String.fromCharCode(65 + index))}: ${String(
          option?.text || option?.actionText || ''
        )}`
    }))
  }), [sourceQuestion]);

  const engineKey = useMemo(
    () => resolveEngineKey(sectionName, traitName, gamifiedQuestion?.gameType),
    [sectionName, traitName, gamifiedQuestion?.gameType]
  );
  const accent = useMemo(
    () => gamifiedQuestion?.scene?.accent || accentByKey(engineKey),
    [engineKey, gamifiedQuestion?.scene?.accent]
  );

  useEffect(() => {
    if (sectionQuestionIndex === 0) {
      // Required trace for section/trait matching validation.
      console.log('[GamifiedQuestionRenderer] sectionName:', sectionName, '| traitName:', traitName);
    }
  }, [sectionQuestionIndex, sectionName, traitName]);

  if (!question) {
    return <div className={styles.empty}>Question unavailable.</div>;
  }

  const handleAnswer = (selectedLabel) => {
    if (!selectedLabel || !question?.id) return;
    onAnswer(question.id, selectedLabel);
  };

  let EngineComponent = LikertSliderEngine;
  if (engineKey === 'ethics') EngineComponent = DilemmaRoomEngine;
  if (engineKey === 'classroom') EngineComponent = ChaosSimulatorEngine;
  if (engineKey === 'digital') EngineComponent = BrokenLessonEngine;
  if (engineKey === 'pedagogical') EngineComponent = LessonBuilderEngine;
  if (engineKey === 'subject') EngineComponent = MisconceptionCatcherEngine;
  if (engineKey === 'openness') EngineComponent = CuriosityExplorerEngine;
  if (engineKey === 'conscientiousness') EngineComponent = PriorityInboxEngine;
  if (engineKey === 'extraversion') EngineComponent = StaffRoomEngine;
  if (engineKey === 'agreeableness') EngineComponent = TrolleyEngine;
  if (engineKey === 'neuroticism') EngineComponent = StressThermometerEngine;
  if (engineKey === 'memory_match') EngineComponent = MemoryMatchEngine;

  return (
    <div className={styles.wrap}>
      <div className={styles.topRow}>
        <div>
          <p className={styles.meta}>
            Question {questionIndex} of {totalQuestions}
          </p>
          <h4 className={styles.title}>{sectionName}</h4>
          {traitName ? <p className={styles.trait}>Trait: {traitName}</p> : null}
        </div>
        <ProgressArc questionIndex={questionIndex} totalQuestions={totalQuestions} />
      </div>

      {sectionQuestionIndex === 0 ? (
        <SectionBanner
          sectionName={sectionName}
          subtitle={`Question 1 of ${Math.max(Number(sectionQuestionCount) || 0, 0)} in this section`}
        />
      ) : null}

      <AnimatePresence mode="wait">
        <motion.div
          key={question?.id || `${sectionName}-${questionIndex}`}
          className={styles.questionCard}
          initial={{ x: 40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        >
          <EngineComponent gamifiedQuestion={gamifiedQuestion} onAnswer={handleAnswer} accent={accent} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default GamifiedQuestionRenderer;
